import React from "react";
import styles from "./divider.module.css";
import { withSafeRender } from "../safe-render";

/**
 * Divider — a native `<hr>` hairline. Server Component, no state and no ARIA,
 * because the browser already exposes `<hr>` as a separator.
 *
 * FUSION ECLIPSE, page UI Kit: Divider - onDark 10188:17180, the one published
 * property being Emphasis = Low | Medium | High. Values in divider.module.css.
 *
 * ⚠ An announced `<hr>` is subject to WCAG 1.4.11 (3:1) and only `high` clears
 * it — low is 1.32:1 — so `decorative` chooses the default: announced defaults
 * to `high`, decorative to Figma's own default, Low.
 *
 * There is no orientation prop; Figma publishes no vertical variant.
 */
export type DividerEmphasis = "low" | "medium" | "high";

export interface DividerProps {
  /**
   * How strongly the line reads. Only `high` clears the WCAG 3:1 floor for a
   * non-text element; see `decorative` for how the default is chosen.
   */
  emphasis?: DividerEmphasis;
  /**
   * Hides the rule from screen readers. Use it when the structure the line
   * suggests is already carried by a heading or a list.
   */
  decorative?: boolean;
  /** Layout only — margins and width. Never colour or weight. */
  className?: string;
}

function DividerImpl({ emphasis, decorative = false, className }: DividerProps) {
  const resolved = emphasis ?? (decorative ? "low" : "high");
  const classes = [styles.divider, styles[resolved], className]
    .filter(Boolean)
    .join(" ");

  return <hr aria-hidden={decorative || undefined} className={classes} />;
}
export const Divider = withSafeRender(DividerImpl, "Divider");

export default Divider;
